import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { localStoragePayhelm } from 'utils'
import axios from 'axios'
import { trafficSettingsUrl } from '@/modules/urls'
import { getAxiosDefaultConfig } from '@/modules/utils'

// Async thunk to load traffic settings from database
export const loadTrafficSettings = createAsyncThunk(
  'trafficSetting/loadTrafficSettings',
  async (_, { rejectWithValue }) => {
    try {

      const response = await axios.get(trafficSettingsUrl, getAxiosDefaultConfig())
      const attributionSettings = response.data || {}

      return attributionSettings
    } catch (error) {
      console.error('Error loading traffic settings:', error)
      // Fallback to localStorage if database fails
      // return getStoredAttributionSettings()
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk to save traffic settings to database
export const saveTrafficSettings = createAsyncThunk(
  'trafficSetting/saveTrafficSettings',
  async (trafficSettings, { rejectWithValue }) => {
    try {

      const tab = trafficSettings.tab // Default to 'ads' if not specified
      const settingsData = trafficSettings.settings || {}

      const response = await axios.post(`${trafficSettingsUrl}?tab=${tab}`, settingsData, getAxiosDefaultConfig())

      if (response.status !== 200) {
        throw new Error('Failed to save traffic settings')
      }

      const result = response.data

      return trafficSettings.settings // Return the original settings for consistency.
    } catch (error) {
      console.error('Error saving traffic settings:', error)
      return rejectWithValue(error.message)
    }
  }
)

const initialState = {
  // attributionSettings: getStoredAttributionSettings(),
  attributionSettings: {
    google_ads: 'first',
    facebook_ads: 'first',
    klaviyo: 'first',
    pinterest: 'first',
    tiktok: 'first',
    amazon: 'first',
  },
  adPlatformOptions: [],
  settingsModalOpen: false,
  loading: false,
  error: null,
  saveStatus: 'idle', // 'idle' | 'pending' | 'fulfilled' | 'rejected'
  metricSettings: {
    metrics: {
      total_sales: true,
      total_sessions: true,
      total_orders: true,
      total_visitors: true,
      total_active_cart: true,
      total_checkouts: true,
      total_pageviews: true,
    }
  }
}

const trafficAdsSettingsSlice = createSlice({
  name: 'trafficSetting',
  initialState,
  reducers: {
    setAttributionSetting: (state, action) => {
      const { platform, value } = action.payload
      state.attributionSettings[platform] = value
      // Save to localStorage as backup (immediate)
      // saveAttributionSettings(state.attributionSettings)
    },
    setAllAttributionSettings: (state, action) => {
      state.attributionSettings = { ...action.payload }
      // Save to localStorage as backup
      // saveAttributionSettings(state.attributionSettings)
    },
    resetAttributionSettings: (state) => {
      const defaultSettings = {
        google_ads: 'first',
        facebook_ads: 'first',
        klaviyo: 'first',
        pinterest: 'first',
        tiktok: 'first',
        amazon: 'first',
      }
      state.attributionSettings = defaultSettings
      // Save to localStorage as backup
      // saveAttributionSettings(defaultSettings)
    },
    setSettingsModalOpen: (state, action) => {
      state.settingsModalOpen = action.payload
    },
    toggleSettingsModal: (state) => {
      state.settingsModalOpen = !state.settingsModalOpen
    },
    // Initialize settings from localStorage (useful for when component mounts)
    initializeAttributionSettings: (state) => {
      // state.attributionSettings = getStoredAttributionSettings()
      state.attributionSettings = {
        google_ads: 'first',
        facebook_ads: 'first',
        klaviyo: 'first',
        pinterest: 'first',
        tiktok: 'first',
        amazon: 'first',
      }
    },
    setAdPlatformOptions: (state, action) => {
      state.adPlatformOptions = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    resetSaveStatus: (state) => {
      state.saveStatus = 'idle'
    },
    setMetricSettings: (state, action) => {
      state.metricSettings = { ...state.metricSettings, ...action.payload }
    },
    setMetricSetting: (state, action) => {
      const { metric, value, tab } = action.payload
      state.metricSettings[tab] = {
        ...state.metricSettings[tab],
        [metric]: value
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Load traffic settings
      .addCase(loadTrafficSettings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loadTrafficSettings.fulfilled, (state, action) => {
        state.loading = false
        state.attributionSettings = action.payload.ads || initialState.attributionSettings
        state.metricSettings = action.payload.metrics || initialState.metricSettings
        state.error = null
      })
      .addCase(loadTrafficSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
        // Keep existing settings if load fails
      })
      // Save traffic settings
      .addCase(saveTrafficSettings.pending, (state) => {
        state.saveStatus = 'pending'
        state.error = null
      })
      .addCase(saveTrafficSettings.fulfilled, (state, action) => {
        state.saveStatus = 'fulfilled'
        // state.attributionSettings = action.payload.ads
        // state.metricSettings = action.payload.metrics
        state.error = null
      })
      .addCase(saveTrafficSettings.rejected, (state, action) => {
        state.saveStatus = 'rejected'
        state.error = action.payload
      })
  },
})

// Action creators
export const {
  setAttributionSetting,
  setAllAttributionSettings,
  resetAttributionSettings,
  setSettingsModalOpen,
  toggleSettingsModal,
  initializeAttributionSettings,
  setAdPlatformOptions,
  clearError,
  resetSaveStatus,
  setMetricSettings,
  setMetricSetting,
} = trafficAdsSettingsSlice.actions

// Selectors
export const selectAttributionSettings = (state) => state.trafficSetting.attributionSettings
export const selectGoogleAdsAttribution = (state) => state.trafficSetting.attributionSettings?.google_ads
export const selectFacebookAdsAttribution = (state) => state.trafficSetting.attributionSettings?.facebook_ads
export const selectKlaviyoAttribution = (state) => state.trafficSetting.attributionSettings?.klaviyo
export const selectPinterestAttribution = (state) => state.trafficSetting.attributionSettings?.pinterest
export const selectTiktokAttribution = (state) => state.trafficSetting.attributionSettings?.tiktok
export const selectAmazonAttribution = (state) => state.trafficSetting.attributionSettings?.amazon
export const selectSettingsModalOpen = (state) => state.trafficSetting.settingsModalOpen
export const selectAdPlatformOptions = (state) => state.trafficSetting.adPlatformOptions
export const selectTrafficSettingsLoading = (state) => state.trafficSetting.loading
export const selectTrafficSettingsError = (state) => state.trafficSetting.error

// Selectors for metric settings
export const selectMetricSettings = (state) => state.trafficSetting.metricSettings
export const selectMetricSetting = (metric) => (state) => state.trafficSetting.metricSettings['metrics']

export const selectSaveStatus = (state) => state.trafficSetting.saveStatus

// Composite selectors
export const selectAttributionForPlatform = (platform) => (state) =>
  state.trafficSetting.attributionSettings[platform]

export const selectHasAttributionSettings = (state) =>
  Object.keys(state.trafficSetting.attributionSettings).length > 0

export const selectIsSaving = (state) => state.trafficSetting.saveStatus === 'pending'

// Export the reducer
export default trafficAdsSettingsSlice.reducer